import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';

import { User } from '@app/modules/model/user';
import { Address } from '@app/modules/model/address';
import { UiWaiterPin } from '@app/components/common/waiter-pin/ui-waiter-pin';

import { alertService } from '@app/modules/common/alert';
import { languageService, languageSupport } from '@app/modules/common/language';
import { dataService } from '@app/modules/data';
import { syncService } from '@app/modules/sync';
import { viewService } from '@app/modules/view';
import { waiterLockService } from '@app/modules/waiter-lock.service';
import { keyboardWedgeService } from '@app/modules/common/keyboard-wedge';
import { AlertModalController } from '@app/modules/common/alert';
import { rfidAssignHelper } from '@app/modules/common/rfid-assign';

/************************************/
/* USER COMPONENT                   */
/************************************/

@Component({
    selector: 'ui-user-edit',
    templateUrl: 'ui-edit.html',
    styleUrls: [
      'ui-edit.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiUserEdit extends languageSupport  implements OnInit, AfterViewInit, OnDestroy {    
    @Input('user') _user: User = null; 

    private _subscriptions = [];

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, private data: dataService, public view: viewService, private modalCtrl: ModalController, private waiterLock: waiterLockService, private wedge: keyboardWedgeService, private sync: syncService) {
        super(lang, change);
    }

    // PIN del owner por NUMPAD (mismo que el bloqueo): teclado numérico, enmascarado
    // con '*'. Se guarda en el control 'pin' (lo envía OnChangesAccept). No se muestra
    // el PIN actual (el server nunca lo devuelve): solo has_pin.
    get PinDisplay(): string {
        let _v = this.UserForm.get('pin').value;
        if (_v){
            return '•'.repeat(('' + _v).length);
        }
        return this._user.has_pin ? this.tr('@user_pin_set') : this.tr('@user_input_pin_ph');
    }

    _oldPin: string = null;     // PIN actual tecleado en el cambio (se envía al server)

    // PULSERA RFID del owner: tag capturado del lector (pendiente de guardar) o
    // petición de quitarla. El tag actual nunca se muestra: solo has_rfid.
    _newRfid: string = null;
    _clearRfid = false;
    _rfidReassign = false;      // la pulsera nueva se le quita al camarero que la tenía (confirmado)
    private _rfidAlert: AlertModalController = null;

    get HasRfid(): boolean {
        if (this._clearRfid){
            return false;
        }
        return !!(this._newRfid || (this._user && this._user.has_rfid));
    }

    // La pulsera del USER solo identifica al DUEÑO de un local (staffpin.php la mira en
    // USER.rfid solo para el owner; a un camarero le identifica STAFF.rfid, desde su ficha
    // de camarero). Sin locales propios, la fila no se muestra.
    get OwnsPlaces(): boolean {
        return !!(this._user && this._user.places && this._user.places.length > 0);
    }

    get RfidDisplay(): string {
        if (this._newRfid){
            return this.tr('@place_waiter_rfid_captured');
        }
        if (this._clearRfid){
            return this.tr('@user_rfid_ph');
        }
        return (this._user && this._user.has_rfid) ? this.tr('@user_rfid_set') : this.tr('@user_rfid_ph');
    }

    // Captura EXCLUSIVA (ver UiWaiterEdit.OpenRfidCapture): el TPV no recibe la lectura.
    async OpenRfidCapture(){
        if (this._rfidAlert){
            return;
        }
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_waiter_rfid_capture_title'),
            message: this.lang.tr('@place_waiter_rfid_capture_message'),
            backdropDismiss: true,
            buttons: [{
                text: this.lang.tr('@place_oneclick_cancel'),
                role: 'cancel'
            }]
        });
        this._rfidAlert = alert;
        this.wedge.Capture().then(async (tag: string) => {
            this._closeRfidCapture();
            if (tag){
                // Relecturas del lector mientras se confirma: no identificar por detrás.
                this.wedge.SuppressTags(true);
                try {
                    // ¿Ya la tiene un camarero de alguno de mis locales? (ver UiWaiterEdit)
                    let _place = (this._user && this._user.places && this._user.places.length > 0) ? this._user.places[0] : null;
                    let _r = _place
                        ? await rfidAssignHelper.Resolve(this.sync, this.lang, this.alertCtrl, _place.objid, 0, 'owner', tag)
                        : { assign: true, reassign: false };
                    if (_r.assign){
                        this._newRfid = tag;
                        this._clearRfid = false;
                        this._rfidReassign = _r.reassign;
                        this.UserForm.markAsDirty();
                    }
                }
                finally {
                    this.wedge.SuppressTags(false);
                }
            }
            this.change.markForCheck();
        });
        alert.onDidDismiss().then(() => {
            this.wedge.CancelCapture();     // resuelve la captura con null (cierra arriba)
        });
        await alert.present();
    }

    private _closeRfidCapture(){
        if (this._rfidAlert){
            let _alert = this._rfidAlert;
            this._rfidAlert = null;
            _alert.dismiss(null).catch(() => {});
        }
    }

    OnClearRfid(event: Event){
        if (event){
            event.stopPropagation();
        }
        this._newRfid = null;
        this._clearRfid = true;
        this.UserForm.markAsDirty();
        this.change.markForCheck();
    }

    private _applyRfidChanges(){
        if (this._newRfid){
            this._user.rfid = this._newRfid;
            this._user.rfidreassign = this._rfidReassign;
        }
        else if (this._clearRfid){
            this._user.clearrfid = true;
        }
        this._newRfid = null;
        this._clearRfid = false;
        this._rfidReassign = false;
    }

    async OpenPinPad(){
        const _modal = await this.modalCtrl.create({
            component: UiWaiterPin,
            // CAMBIO de PIN: pide el actual SOLO si ya hay uno (has_pin). El server
            // verifica el actual antes de aceptar el nuevo.
            componentProps: { place: null, changeMode: true, needsOld: this._user.has_pin, lockMode: false },
            cssClass: 'waiter-pin-modal'
        });
        // Si el bloqueo saltó durante el await de create, no presentar el numpad.
        if (this.waiterLock.IsLocked){
            await _modal.dismiss(null).catch(() => {});
            return;
        }
        // Si salta el bloqueo con el numpad abierto, cerrarlo (ver ui.edit.ts): si
        // no, queda apilado bajo el modal de bloqueo y la confirmación podría caer
        // en el numpad de captura.
        let _lockSub = this.waiterLock.OnLockRequired.subscribe(() => {
            _modal.dismiss(null).catch(() => {});
        });
        _modal.onDidDismiss().then((detail: any) => {
            _lockSub.unsubscribe();
            this.waiterLock.SetModalActive(false);      // reanudar el bloqueo
            let _pin = (detail && detail.data) ? detail.data.pin : null;
            if (_pin){
                this.UserForm.get('pin').setValue(_pin);
                this._oldPin = (detail.data.old) ? detail.data.old : null;
                this.UserForm.markAsDirty();
                this.change.markForCheck();
            }
        });
        this.waiterLock.SetModalActive(true);
        await _modal.present();
    }

    OnViewChanged(){
        this.change.markForCheck();
    }

    ngOnInit(){
        this._subscriptions.push(
            this._user.OnRefresh.subscribe(
            data => {
                if (this.UserForm.pristine){
                    this.LoadUserForm();
                }
                this.change.markForCheck();
            })
        );
    }

    ngAfterViewInit(){
        this.change.markForCheck();
    }

    ngOnDestroy(){
        super.OnDestroy();
        this._closeRfidCapture();

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
    }

    @Output('onClose') _onClose = new EventEmitter<any>();     
    OnClose(){
        this._onClose.emit();
    }

    /**********************************/
    /* USER VIEW                      */
    /**********************************/

    private _nophoto = '/assets/image/user.png';

    private _languages = null;
    get SupportedLanguages(){
        if (!this._languages) {
            this._languages = [];

            let _lang = this.lang.GetLanguages();            
            for(let i=0; i < _lang.length; i++) {
                this._languages.push({
                    text: this.lang.tr('@language_' + _lang[i]['lang']),
                    cssicon: "fi fi-" + _lang[i]['flag'].toLowerCase(),
                    value: _lang[i]['lang']
                });
            }
        }

        return this._languages;
    }

    get LanguageFlag(){
        let _value = this.UserForm.get('language').value;
        for (let _lang of this.lang.GetLanguages()){
            if (_value && (_lang.lang == _value.value)){
                return "fi fi-" + _lang.flag.toLowerCase() + " fis";
            }
        }
        return "fi";     // not found 
    }

    private get _CurrentLanguage(){
        let _lang = this.lang.GetLanguage(true);
        return {
            text: this.lang.tr('@language_' + _lang),
            value: _lang
        }
    }    

    private checkAvailableUser(control: FormControl): any {
        let _username = control.value;
        if (_username && (_username != this._user.email)){
          this.data.Available(_username).then(
          data => {
            if (!data && control.valid){
              control.setErrors({ available: true });    
            }
          });
        }
        
        return null;
    }
    
    private _userForm = null;   
    get UserForm(){
        if (this._userForm == null){
            this.LoadUserForm();
        }
        return this._userForm;
    }

    private LoadUserForm(){
        let _language = this._CurrentLanguage;
        let _photo = this._user.photo || this._nophoto;
        let _firstname =  this._user.firstname;
        let _lastname = this._user.lastname;
        let _phonenumber = this._user.phone;
        let _email = this._user.email;

        if (this._userForm == null){
            this._userForm = new FormGroup({
                language: new FormControl(_language, [
                    Validators.required,                
                ]),
                photo: new FormControl(_photo, [
                    // no validators required
                ]),
                firstname: new FormControl(_firstname, [
                    // no validators required
                ]),
                lastname: new FormControl(_lastname, [
                    // no validators required
                ]),
                phonenumber: new FormControl(_phonenumber, [
                    // no validators required
                ]),            
                email: new FormControl(_email, [
                    Validators.required,
                    Validators.email,
                    this.checkAvailableUser.bind(this)
                ]),
                // PIN del owner (opcional): vacío = no cambiar. 4-8 dígitos.
                pin: new FormControl(null, [
                    Validators.pattern(/^[0-9]{4,8}$/)
                ])
            });
        }
        else {
            this._userForm.patchValue({
                language: _language,
                photo: _photo,
                firstname: _firstname,
                lastname: _lastname,
                phonenumber: _phonenumber,
                email: _email,
                pin: null
            });
        }

        this._userForm.markAsPristine();
        setTimeout(() => {
            this._userForm.markAsPristine();
        }, 0);
    }

    async UpdateLanguage(){
        let _subscription = this.lang.OnSetLanguage.subscribe(
        data => {
            _subscription.unsubscribe();
            this.change.markForCheck();
        });
        
        let _value = this.UserForm.get('language').value;
        if (_value) {
            let _lang = _value.value;
            if (_lang && (this._user.lang != _lang)){
                this._user.lang = _value.value;
                if (this._user.DoCommit()){
                    this.lang.SetLanguage(this._user.lang);
                }    
            }
        }
    }
    
    private async OnMailChanged(newmail){
        return new Promise((resolve) => {
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@user_data_email_changed_title'),
                message: this.lang.tr('@user_data_email_changed_message', [ newmail ]),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@ok'),
                    handler: () => {
                        this._user.email = newmail;
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
    
                        return false;
                    }
                }]
            });
    
            alert.onDidDismiss().then(
            (data) => {
                resolve(true);
            });

            alert.present();            
        })
    }

    /* TODO:
        Añadir los deliveries en el user form
        Solo son necesarios si el acceso es de delivery 
    */

    OnAddAddress(){
        let _address = new Address(null, this.data);
        if (_address){
            this._user.AddDelivery(_address);
        }
        this.change.markForCheck();
    }
    
    OnAddressChanged(){
        console.log("pasa por aqui: OnAddressChanged"); // TODO
    }

    async OnChangesAccept(){
        let _newmail = this.UserForm.get('email').value;
        if (this._user.email == _newmail){
            _newmail = null;
        }
        this._user.email = this.UserForm.get('email').value;
        this.UserForm.get('email').setValue(this._user.email);

        this._user.photo = this.UserForm.get('photo').value;
        if (this._user.photo == this._nophoto){
            this._user.photo = this._user.base64 = null;
        }

        this._user.lang = this.UserForm.get('language').value.value;
        this._user.firstname = this.UserForm.get('firstname').value;
        this._user.lastname = this.UserForm.get('lastname').value;
        this._user.phone = this.UserForm.get('phonenumber').value;

        // PIN del owner: solo si se ha introducido uno nuevo (vacío = no cambiar).
        // El server lo hashea; nunca mostramos el actual (solo has_pin).
        let _pin = this.UserForm.get('pin').value;
        if (_pin){
            this._user.pin = _pin;
            this._user.oldpin = this._oldPin;   // el server verifica el actual
        }
        this.UserForm.get('pin').reset();
        this._oldPin = null;
        this._applyRfidChanges();

        if (_newmail){
            await this.OnMailChanged(_newmail);
        }

        this.UserForm.markAsPristine();
        if (this._user.DoCommit()){
            this.OnClose();
        }
    }

    private _OnCancel(){
        this.UserForm.setValue({
            language: this._CurrentLanguage,
            photo: this._user.photo || this._nophoto,
            firstname: this._user.firstname,
            lastname: this._user.lastname,
            phonenumber: this._user.phone,
            email: this._user.email,
            pin: null
        });

        this.OnClose();
    }

    async OnChangesCancel(){
        if (this.UserForm.dirty){
            console.log("[EDIT USER] confirm changes lost");
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@changes_lost_confirm_tittle'),
                message: this.lang.tr('@changes_lost_confirm_message'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);

                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);

                        return false;
                    }
                }]
            });

            alert.onDidDismiss().then((detail:any) => {
                if (detail.data == true){
                    this._OnCancel();
                }
            });

            await alert.present();        
        }
        else {
            this._OnCancel();
        }
    }
    
    /**********************************/
    /* PASSWORD CHANGE                */
    /**********************************/

    private _onpassword = false;
    get onpassword(){
        return this._onpassword;
    }

    set onpassword(value){
        if (value != this._onpassword){
            this._onpassword = value;
            if (this._onpassword == false){
                this.PasswordForm.reset();
            }
        }

        this.change.markForCheck();
    }

    private _invalid = false;
    get isinvalid(){
        return this._invalid;
    }

    set isinvalid(value){
        if (value != this._invalid){
            this._invalid = value;
        }

        this.change.markForCheck();
    }

    private _onrequest = false;
    get onrequest(){
        return this._onrequest;
    }

    set onrequest(value){
        if (value != this._onrequest){
            this._onrequest = value;
        }

        this.change.markForCheck();
    }

    private checkConfirmPassword(group: FormGroup):any {
        let _password = group.get('password');
        let _confirm = group.get('confirm');
        
        if (_password && _confirm){
            if (_password.value !== _confirm.value){
                _confirm.setErrors({ 'nomatch': true });
            }
        }
        
        return null;  
    }
    
    private _passwordForm = null;
    get PasswordForm() {
        if (!this._passwordForm){
            this._passwordForm = new FormGroup({
                previous: new FormControl(null, [
                    Validators.required,
                    Validators.minLength(8)                        
                ]),
                password: new FormControl(null, [
                    Validators.required,
                    Validators.minLength(8)                        
                ]),
                confirm: new FormControl(null, [
                    Validators.required,
                    Validators.minLength(8)                        
                ])
            }, this.checkConfirmPassword);
        }
        return this._passwordForm;
    }
    
    UpdatePasword(){
        this.onpassword = true;
    }

    OnOldPassword(){
        this.isinvalid = false;
    }
    
    async OnPasswordAccept(){
        let _completed = false;

        let _oldpassword = this._passwordForm.get('previous').value;
        let _newpassword = this._passwordForm.get('password').value;    

        this.onrequest = true;
        if (!_completed){   // first try double-CRC login
            let _promise = new Promise((resolve) => {
                this.data.DoPassword(_oldpassword, _newpassword, false).then(
                response => {
                    if (response){    // success
                        this.onpassword = false;
                        resolve(true);
                    }
                    else {  // invalid password
                        resolve(false);
                    }
                });
             });
      
             this.isinvalid = !await _promise;
             _completed = !this.isinvalid;
        }

        if (!_completed){   // next try single-CRC login
            let _promise = new Promise((resolve) => {
                this.data.DoPassword(_oldpassword, _newpassword, true).then(
                response => {
                    if (response){    // success
                        this.onpassword = false;
                        resolve(true);
                    }
                    else {  // invalid password
                        resolve(false);
                    }
                });
            })
        
            this.isinvalid = !await _promise;
            _completed = !this.isinvalid;
        }
    
        this.onrequest = false;   
    }

    OnPasswordCancel(){
        this.onpassword = false;
    }

    /* show / hide password value */

    private _showpasswords = [ false, false, false ];
    
    get ShowPassword0(){
        return this._showpasswords[0];
    }

    get ShowPassword1(){
        return this._showpasswords[1];
    }

    get ShowPassword2(){
        return this._showpasswords[2];
    }

    OnShowPassword0(){
        this._showpasswords[0] = !this._showpasswords[0];
        this.change.markForCheck();
    }   

    OnShowPassword1(){
        this._showpasswords[1] = !this._showpasswords[1];
        this.change.markForCheck();
    }    

    OnShowPassword2(){
        this._showpasswords[2] = !this._showpasswords[2];
        this.change.markForCheck();
    }      
}
